import * as THREE from "three";
import {SetupObstUI} from "./TransControl";
import { OBB } from 'three/examples/jsm/math/OBB';
import {Matrix3, Matrix4, Plane, Triangle, Vector3} from "three";
import { EditAction } from "../../../../../@types/enums";

export function SetWallMeshArr(self, interObj) {
    const {tabInfo} = self;
    if (interObj && tabInfo === 'Wall Sliding' && interObj.object.name.includes('wallMesh')) {
        const {select} = interObj.object;
        interObj.object.select = !select;
        SetSelWallMeshArr(self);
    }
}

function SetSelWallMeshArr(self) {
    const {wallMeshArr} = self, selWallArr = [];
    wallMeshArr.forEach(wallMesh => {
        const frameMesh = wallMesh.parent.children[1];
        frameMesh.visible = wallMesh.select;
        if (wallMesh.select) {
            selWallArr.push(wallMesh);
        }
    });
    self.selWallArr = selWallArr.length ? selWallArr : wallMeshArr;
}

export function ResetWallMeshArr(self) {
    self.wallMeshArr.forEach(wallMesh => {
        wallMesh.select = false;
        wallMesh.parent.children[1].visible = false;
    });
    self.obstGroup.children.forEach(group => {
        const inGroup = group.children[0].children[0];
        inGroup.children[0].select = false;
        inGroup.children[1].visible = false;
    });
    SetSelWallMeshArr(self);
    SetupObstUI(self);
}


const updateMeshTexture = (meshArr, map, color, type, self) => {
    const uuidArr = [], olderArr = [], newerArr = [];
    meshArr.forEach(mesh => {
        const oldMat = mesh.material, oldMap = oldMat.map, oldColor = oldMat.color.getHex();
        uuidArr.push(mesh.uuid);
        olderArr.push({map: oldMap, color: oldColor});
        newerArr.push({map, color});
        // mesh.material.dispose();
        var material;
        if (type === 'map') material = new THREE.MeshStandardMaterial({map});
        else if (type === 'color') material = new THREE.MeshStandardMaterial({color});
        mesh.material = material;
        mesh.oriMap = material.map;
        mesh.oriColor = material.color.getHex();
    })
    self.addStep(EditAction.MaterialChange, uuidArr, olderArr, newerArr);
};

export function ChangeMeshMaterial(meshArr, value, type, self, meshName, textureId) {
    if (meshArr.length === 0) return;

    if (type === 'map') {
        new THREE.TextureLoader().load(value, (map) => {
            if (meshName === 'wall') {
                //rotate texture 90 degrees
                map.rotation = Math.PI / 2;
            }
            map.wrapS = map.wrapT = THREE.RepeatWrapping;

            updateMeshTexture(meshArr, map, null, type, self)

            for (const mesh of meshArr) {
                mesh.userData.textureId = textureId;
                mesh.userData.color = undefined;
            }
        });

    } else if (type === 'color') {
        const color = new THREE.Color(value);
        updateMeshTexture(meshArr, null, color, type, self)

        for (const mesh of meshArr) {
            mesh.userData.textureId = undefined;
            mesh.userData.color = color;
        }
    }
}

function isPolygonObstructionMesh(mesh) {
    return mesh?.shapeNum === 4 || mesh?.geometry?.type === 'BufferGeometry';
}

function convexHullXZ(points) {
    const pts = [...points].sort((a, b) => a.x - b.x || a.z - b.z);
    if (pts.length <= 2) return pts;
    const cross = (o, a, b) => (a.x - o.x) * (b.z - o.z) - (a.z - o.z) * (b.x - o.x);
    const lower = [];
    for (const p of pts) {
        while (lower.length >= 2 && cross(lower[lower.length - 2], lower[lower.length - 1], p) <= 0) lower.pop();
        lower.push(p);
    }
    const upper = [];
    for (let i = pts.length - 1; i >= 0; i--) {
        const p = pts[i];
        while (upper.length >= 2 && cross(upper[upper.length - 2], upper[upper.length - 1], p) <= 0) upper.pop();
        upper.push(p);
    }
    lower.pop();
    upper.pop();
    return lower.concat(upper);
}

/**
 * Footprint meshes are extruded from world nodes in local XZ. Those quads are often
 * a few degrees off the roof axes, so fromBox3() looks like a skewed box around the
 * true node rectangle. Fit a min-area OBB to hull edges so it matches the mesh.
 * Scaled BoxGeometry obsts still use fromBox3().
 */
function obbForRectangleMesh(mesh) {
    const geometry = mesh.geometry;
    geometry.computeBoundingBox();
    const aabbObb = new OBB().fromBox3(geometry.boundingBox);
    if (!mesh?.userData?.footprintFromNodes) return aabbObb;

    const pos = geometry.getAttribute('position');
    if (!pos || pos.count < 2) return aabbObb;

    const seen = new Set();
    const points = [];
    const vertex = new Vector3();
    let minY = Infinity;
    let maxY = -Infinity;
    for (let i = 0; i < pos.count; i++) {
        vertex.fromBufferAttribute(pos, i);
        minY = Math.min(minY, vertex.y);
        maxY = Math.max(maxY, vertex.y);
        const key = `${vertex.x.toFixed(4)},${vertex.z.toFixed(4)}`;
        if (seen.has(key)) continue;
        seen.add(key);
        points.push({ x: vertex.x, z: vertex.z });
    }
    if (points.length < 2) return aabbObb;

    const hull = convexHullXZ(points);
    const edgeCount = hull.length >= 2 ? hull.length : 0;
    let best = null;
    for (let i = 0; i < edgeCount; i++) {
        const a = hull[i];
        const b = hull[(i + 1) % hull.length];
        const dx = b.x - a.x;
        const dz = b.z - a.z;
        const len = Math.hypot(dx, dz);
        if (len < 1e-8) continue;
        const axisX = { x: dx / len, z: dz / len };
        const axisZ = { x: -axisX.z, z: axisX.x };
        let minU = Infinity;
        let maxU = -Infinity;
        let minW = Infinity;
        let maxW = -Infinity;
        for (const point of points) {
            const u = point.x * axisX.x + point.z * axisX.z;
            const w = point.x * axisZ.x + point.z * axisZ.z;
            minU = Math.min(minU, u);
            maxU = Math.max(maxU, u);
            minW = Math.min(minW, w);
            maxW = Math.max(maxW, w);
        }
        const sizeU = maxU - minU;
        const sizeW = maxW - minW;
        const area = sizeU * sizeW;
        if (!best || area < best.area) {
            best = { axisX, axisZ, minU, maxU, minW, maxW, sizeU, sizeW, area };
        }
    }
    if (!best) return aabbObb;

    const centerU = (best.minU + best.maxU) / 2;
    const centerW = (best.minW + best.maxW) / 2;
    const center = new Vector3(
        best.axisX.x * centerU + best.axisZ.x * centerW,
        (minY + maxY) / 2,
        best.axisX.z * centerU + best.axisZ.z * centerW,
    );
    const halfSize = new Vector3(
        Math.max(best.sizeU / 2, 1e-4),
        Math.max((maxY - minY) / 2, 1e-4),
        Math.max(best.sizeW / 2, 1e-4),
    );
    const rotation = new Matrix3().set(
        best.axisX.x, 0, best.axisZ.x,
        0, 1, 0,
        best.axisX.z, 0, best.axisZ.z,
    );
    return new OBB(center, halfSize, rotation);
}

const _obbAxisX = new Vector3();
const _obbAxisY = new Vector3();
const _obbAxisZ = new Vector3();
const _obbWorldRot = new Matrix3();


function applyFootprintObbWorldMatrix(obb, matrixWorld) {
    const e = matrixWorld.elements;
    let sx = _obbAxisX.set(e[0], e[1], e[2]).length() || 1;
    const sy = _obbAxisY.set(e[4], e[5], e[6]).length() || 1;
    const sz = _obbAxisZ.set(e[8], e[9], e[10]).length() || 1;
    if (matrixWorld.determinant() < 0) sx = -sx;

    _obbWorldRot.setFromMatrix4(matrixWorld);
    const te = _obbWorldRot.elements;
    const invSX = 1 / sx;
    const invSY = 1 / sy;
    const invSZ = 1 / sz;
    te[0] *= invSX; te[1] *= invSX; te[2] *= invSX;
    te[3] *= invSY; te[4] *= invSY; te[5] *= invSY;
    te[6] *= invSZ; te[7] *= invSZ; te[8] *= invSZ;

    obb.center.applyMatrix4(matrixWorld);
    obb.rotation.premultiply(_obbWorldRot);
    obb.halfSize.x *= Math.abs(sx);
    obb.halfSize.y *= sy;
    obb.halfSize.z *= sz;
}

export function SetMeshOBB(mesh) {
    if (!mesh?.geometry) {
        mesh.userData.obb = new OBB();
        return;
    }
    mesh.geometry.computeBoundingBox();

    if (isPolygonObstructionMesh(mesh)) {
        // polygon shape mesh (geometry is buffer geometry)
    } else {
        mesh.geometry.userData.obb = obbForRectangleMesh(mesh);
    }

    mesh.userData.obb = new OBB();
}

export function SetWallMeshOBB(wall, center, firstPos) {
    SetMeshOBB(wall);
    wall.geometry.userData.obb.center = center.clone().sub(firstPos);

    wall.userData.center = center.clone();
    wall.userData.firstPos = {...firstPos};
}

export function UpdateMeshOBB(mesh, spacing) {
    if (!mesh?.geometry) return;
    if (isPolygonObstructionMesh(mesh)) {
        return;
    }
    if (!mesh.geometry.userData?.obb) {
        SetMeshOBB(mesh);
    }
    if (!mesh.userData.obb) {
        mesh.userData.obb = new OBB();
    }
    mesh.updateWorldMatrix(true, false);
    mesh.userData.obb.copy(mesh.geometry.userData.obb);
    if (mesh.userData.footprintFromNodes) {
        applyFootprintObbWorldMatrix(mesh.userData.obb, mesh.matrixWorld);
    } else {
        mesh.userData.obb.applyMatrix4(mesh.matrixWorld);
    }

    if (spacing) {
        mesh.userData.obb.halfSize.addScalar(spacing / 2);
    }
}

export function UpdateWallMeshOBB(wall, spacing) {
    wall.updateWorldMatrix(true, false);
    UpdateMeshOBB(wall, spacing);
    wall.userData.obb.center = wall.userData.center;
}

export function DoesIntersectOBB(mesh1, mesh2) {


    if(mesh2.shapeNum===4 || mesh2.geometry.type === 'BufferGeometry') {

        const vertices = mesh2.geometry.attributes.position.array;
        const indices = mesh2.geometry.index.array;

        const triangle = new Triangle();
        const a = new Vector3();
        const b = new Vector3();
        const c = new Vector3();

        for (let i = 0; i < indices.length; i += 3) {
            a.fromArray(vertices, indices[i] * 3);
            b.fromArray(vertices, indices[i + 1] * 3);
            c.fromArray(vertices, indices[i + 2] * 3);

            triangle.set(a, b, c);

            if (mesh1.userData.obb.intersectsTriangle(triangle)) {
                return true;
            }
        }


        const mesh1Vertices = mesh1.geometry.attributes.position.array;
        const pointInside = new Vector3();

        for (let i = 0; i < mesh1Vertices.length; i += 3) {
            pointInside.fromArray(mesh1Vertices, i);
            pointInside.applyMatrix4(mesh1.matrixWorld);

            if ( isPointInsidePolygon(pointInside, mesh2)) {
                return true;
            }


        }


        const raycaster1 = new THREE.Raycaster();
        const raycaster2 = new THREE.Raycaster();
        const center = new Vector3();
        center.copy(mesh1.userData.obb.center);


        const upwardCenter = new Vector3();
        upwardCenter.copy(center)
        upwardCenter.y -= 1;

        const downwardCenter = new Vector3();
        downwardCenter.copy(center)
        downwardCenter.y += 1;

        const upwardDir = new Vector3(0, 1, 0).normalize()

        raycaster1.set(upwardCenter, upwardDir);


        const downwardDir = new Vector3(0, -1, 0).normalize()
        raycaster2.set(downwardCenter, downwardDir);


        const intersects = raycaster1.intersectObject(mesh2);
        const intersects2 = raycaster2.intersectObject(mesh2);


        const combinedIntersects = intersects.concat(intersects2).filter(intersect => intersect.object.geometry.type !== "EdgesGeometry");


        if (combinedIntersects.length > 0) {
            return true;
        }

    }


    return mesh1.userData.obb.intersectsOBB(mesh2.userData.obb);
}



function isPointInsidePolygon(point, polygonMesh) {
    const vertices = polygonMesh.geometry.attributes.position.array;
    const indices = polygonMesh.geometry.index.array;

    const triangle = new Triangle();
    const a = new Vector3();
    const b = new Vector3();
    const c = new Vector3();
    const plane = new Plane();

    let inside = true;

    // Check if the point is inside all the triangles
    for (let i = 0; i < indices.length; i += 3) {
        a.fromArray(vertices, indices[i] * 3);
        b.fromArray(vertices, indices[i + 1] * 3);
        c.fromArray(vertices, indices[i + 2] * 3);

        triangle.set(a, b, c);
        plane.setFromCoplanarPoints(a, b, c);

        if (plane.distanceToPoint(point) > 0) {
            inside = false;
            break;
        }
    }

    return inside;
}



function getOBBInverseMatrix(obb, target = new Matrix4()) {

    target.identity();
    const rotationMatrix4 = new Matrix4();
    rotationMatrix4.setFromMatrix3(obb.rotation);

    target.multiply(new Matrix4().makeTranslation(obb.center.x, obb.center.y, obb.center.z));
    target.multiply(rotationMatrix4);

    target.invert();

    return target;
}

function aabbIntersectsTriangle(aabbHalfSize, v0, v1, v2) {


    const minMax = (out, ...values) => {
        out[0] = Math.min(...values);
        out[1] = Math.max(...values);
    };

    let r0 = v0.x, r1 = v1.x, r2 = v2.x;
    let rangeX = [0, 0]; // [min, max]
    minMax(rangeX, r0, r1, r2);
    if (rangeX[1] < -aabbHalfSize.x || rangeX[0] > aabbHalfSize.x) {
        return false;
    }

    r0 = v0.y; r1 = v1.y; r2 = v2.y;
    let rangeY = [0, 0];
    minMax(rangeY, r0, r1, r2);
    if (rangeY[1] < -aabbHalfSize.y || rangeY[0] > aabbHalfSize.y) {
        return false;
    }

    r0 = v0.z; r1 = v1.z; r2 = v2.z;
    let rangeZ = [0, 0];
    minMax(rangeZ, r0, r1, r2);
    if (rangeZ[1] < -aabbHalfSize.z || rangeZ[0] > aabbHalfSize.z) {
        return false;
    }

    const f1 = new Vector3();
    const f2 = new Vector3();
    const normal = new Vector3();

    const e0 = new Vector3().subVectors(v1, v0);
    const e1 = new Vector3().subVectors(v2, v1);
    const e2 = new Vector3().subVectors(v0, v2);

    normal.crossVectors(e0, e1);


    const r =
        aabbHalfSize.x * Math.abs(normal.x) +
        aabbHalfSize.y * Math.abs(normal.y) +
        aabbHalfSize.z * Math.abs(normal.z);

    // Project v0 (v1, v2) on the normal, check min/max
    const dist0 = normal.dot(v0);
    const dist1 = normal.dot(v1);
    const dist2 = normal.dot(v2);
    const triMin = Math.min(dist0, dist1, dist2);
    const triMax = Math.max(dist0, dist1, dist2);

    if (triMax < -r || triMin > r) {
        return false; // no overlap
    }


    const testAxis = (axis, v0, v1, v2, aabbHalf) => {

        const p0 = v0.dot(axis);
        const p1 = v1.dot(axis);
        const p2 = v2.dot(axis);
        const minP = Math.min(p0, p1, p2);
        const maxP = Math.max(p0, p1, p2);


        const rAABB =
            aabbHalf.x * Math.abs(axis.x) +
            aabbHalf.y * Math.abs(axis.y) +
            aabbHalf.z * Math.abs(axis.z);

        if (maxP < -rAABB || minP > rAABB) {
            return false;
        }
        return true;
    };

    const aabbAxes = [
        new Vector3(1,0,0),
        new Vector3(0,1,0),
        new Vector3(0,0,1),
    ];


    for (const edge of [ e0, e1, e2 ]) {
        for (const axis of aabbAxes) {
            const crossAxis = new Vector3().crossVectors(edge, axis);
            if (crossAxis.lengthSq() < 1e-10) {
                continue;
            }
            if (!testAxis(crossAxis.normalize(), v0, v1, v2, aabbHalfSize)) {
                return false;
            }
        }
    }
    return true;
}


export function patchOBBForTriangleIntersection(OBBClass) {
    OBBClass.prototype.intersectsTriangle = function(tri) {
        const invMat = getOBBInverseMatrix(this);

        const v0 = tri.a.clone().applyMatrix4(invMat);
        const v1 = tri.b.clone().applyMatrix4(invMat);
        const v2 = tri.c.clone().applyMatrix4(invMat);

        return aabbIntersectsTriangle(this.halfSize, v0, v1, v2);
    };
}


export function disposeObject(object) {
    // Traverse all descendants and dispose of their resources
    object.traverse(child => {
        if (child.isMesh) {
            // Dispose of geometry and material resources
            if (child.geometry) {
                child.geometry.dispose();
            }
            if (child.material) {
                // Check if the material is an array or a single material
                if (Array.isArray(child.material)) {
                    for (const material of child.material) {
                        disposeMaterial(material);
                    }
                } else {
                    disposeMaterial(child.material);
                }
            }
        }
    });

    // Remove all children from the object itself (optional, as traverse handles them,
    // but ensures the object is empty if it's reused)
    while (object.children.length > 0) {
        disposeObject(object.children[0]);
    }

    // Remove the object from its parent
    if (object.parent) {
        object.parent.remove(object);
    }
}


export function disposeMaterial(material) {
    // Dispose of textures associated with the material
    for (const key of Object.keys(material)) {
        const value = material[key];
        if (value && value.isTexture) {
            value.dispose();
        }
    }
    // Dispose of the material itself
    material.dispose();
}
